import { ColumnIndex } from "@tento-ui/components/sheet/column-index";
import type { ScrollEngine } from "@tento-scroll/engine/scroll-engine";
import { SheetDelegate } from "@tento-lona/sheets-ui/sheet/sheet-delegate.ts";
import { SheetLegend } from "@tento-lona/sheets-ui/sheet/sheet-legend.ts";
import {
  type BindCellContext,
  type RenderPlugin,
  RowOptionsElement,
  type SheetLegendClass,
  type StaticChildSpec,
} from "@tento-lona/sheets-ui/sheet/render-plugin.ts";
import { GridIcon, LonaIcons } from "@tento-ui/ui/icons";
import type { Option, RowLocalId } from "@tento-lona/sheets";
import { type SheetRow } from "@tento-lona";
import { CalendarMonthHost } from "./grid/month/calendar-month.ts";
import { CalendarMonthGesture } from "@tento-ui/components/calendar-month/calendar-month-gesture";
import { GRID_LAYOUT_SPEC } from "@tento-lona/sheets/platform";
import type { PerTypeSpec } from "@tento-lona/sheets/platform";
import { discoverCalendarMonthSubcolumns } from "./grid/month/discover-subcolumns.ts";

/** The layout plugin for a month grid. */
export class GridLayoutPlugin
  implements RenderPlugin.Layout<HTMLDivElement, SheetLegend.DefaultRow>
{
  static readonly spec: PerTypeSpec = GRID_LAYOUT_SPEC;

  readonly role = "layout";
  readonly id = "grid";
  readonly icon: HTMLTemplateElement = LonaIcons.$template(GridIcon);

  static readonly #VIEWPORT_MONTH_DEBOUNCE_MS = 300;

  #calendarsByRowId = new Map<RowLocalId, CalendarMonthHost>();
  #lastBoundMonthByRowId = new Map<RowLocalId, string>();
  #pendingMonthBindByRowId = new Map<RowLocalId, number>();
  #pendingMonthKeyByRowId = new Map<RowLocalId, string>();
  #extraGestures: CalendarMonthGesture<CalendarMonthGesture.Info>[] = [];

  readonly #calendarMonthDeps: CalendarMonthHost.Deps;
  constructor(
    public readonly makeRowOptions: RowOptionsElement.Factory,
    calendarMonthDeps: CalendarMonthHost.Deps,
  ) {
    this.#calendarMonthDeps = calendarMonthDeps;
  }

  addGesture(gesture: CalendarMonthGesture<CalendarMonthGesture.Info>): void {
    this.#extraGestures.push(gesture);
    for (const calendar of this.#calendarsByRowId.values()) {
      calendar.addGesture(gesture);
    }
  }

  #getOrCreateCalendar(rowId: RowLocalId): CalendarMonthHost {
    let calendar = this.#calendarsByRowId.get(rowId);
    if (!calendar) {
      calendar = new CalendarMonthHost(this.#calendarMonthDeps);
      for (const gesture of this.#extraGestures) {
        calendar.addGesture(gesture);
      }
      this.#calendarsByRowId.set(rowId, calendar);
    }
    return calendar;
  }

  #currentMonthForColumns(
    columns: ColumnIndex.Key.Resolved.Default.Range,
  ) {
    return columns.start.discrete()[0].start;
  }

  #monthKeyForColumns(
    columns: ColumnIndex.Key.Resolved.Default.Range,
  ): string {
    const currentMonth = this.#currentMonthForColumns(columns);
    return `${currentMonth.yr}-${currentMonth.mth}`;
  }

  #cancelPendingMonthBind(rowId: RowLocalId): void {
    const timer = this.#pendingMonthBindByRowId.get(rowId);
    if (timer != null) window.clearTimeout(timer);
    this.#pendingMonthBindByRowId.delete(rowId);
    this.#pendingMonthKeyByRowId.delete(rowId);
  }

  #shouldDebounceMonthBind(
    viewportChangeSource: ScrollEngine.ViewportChangeSource,
  ): boolean {
    return viewportChangeSource === "scroll" ||
      viewportChangeSource === "snap";
  }

  #bindMonth(
    row: SheetRow.Joined.Handle,
    columns: ColumnIndex.Key.Resolved.Default.Range,
    handle: SheetDelegate,
  ): void {
    const currentMonth = this.#currentMonthForColumns(columns);
    const monthKey = this.#monthKeyForColumns(columns);

    this.#cancelPendingMonthBind(row.id);
    this.#lastBoundMonthByRowId.set(row.id, monthKey);

    const today = columns.start.tz.today().date.start.date;
    const height = GridLayoutPlugin.height(row) - 4;

    const calendar = this.#getOrCreateCalendar(row.id);
    const subcolumns = discoverCalendarMonthSubcolumns(
      row,
      handle.pluginManager,
      calendar,
      this.#calendarMonthDeps.coordinator,
    );

    calendar.bind({
      month: currentMonth,
      today,
      tz: columns.start.tz,
      height,
      handle,
      rowId: row.id,
      row,
      subcolumns,
    });

    handle.invalidateStaticChildren();
  }

  #scheduleMonthBind(
    row: SheetRow.Joined.Handle,
    columns: ColumnIndex.Key.Resolved.Default.Range,
    handle: SheetDelegate,
    monthKey: string,
  ): void {
    const rowId = row.id;
    const sheetId = handle.state.sheet.id.toString();

    this.#cancelPendingMonthBind(rowId);
    this.#pendingMonthKeyByRowId.set(rowId, monthKey);
    this.#pendingMonthBindByRowId.set(
      rowId,
      window.setTimeout(() => {
        this.#pendingMonthBindByRowId.delete(rowId);

        if (handle.state.sheet.id.toString() !== sheetId) {
          this.#pendingMonthKeyByRowId.delete(rowId);
          return;
        }

        if (this.#pendingMonthKeyByRowId.get(rowId) !== monthKey) return;

        const visibleColumns = handle.state.visibleColumns;
        if (
          !visibleColumns ||
          this.#monthKeyForColumns(visibleColumns) !== monthKey
        ) {
          this.#pendingMonthKeyByRowId.delete(rowId);
          return;
        }

        const rowStillVisible = handle.state.sheet.joinedTree.flattened.rows.some(
          (candidate) => candidate.id === rowId,
        );
        if (!rowStillVisible) {
          this.#pendingMonthKeyByRowId.delete(rowId);
          return;
        }

        this.#pendingMonthKeyByRowId.delete(rowId);
        this.#bindMonth(row, columns, handle);
      }, GridLayoutPlugin.#VIEWPORT_MONTH_DEBOUNCE_MS),
    );
  }

  optionsInfo(_row: SheetRow.Joined.Handle): Option<string> {
    return "Display a monthly calendar that automatically updates based on the viewport position. Perfect for scheduling and date-based views.";
  }

  makeCell(): HTMLDivElement {
    return document.createElement("div");
  }

  bindCell(_$cell: HTMLDivElement, _context: BindCellContext) {}

  onViewportColumnsChanged(
    row: SheetRow.Joined.Handle,
    columns: ColumnIndex.Key.Resolved.Default.Range,
    handle: SheetDelegate,
    _$legend: Option<SheetLegend.DefaultRow>,
    viewportChangeSource: ScrollEngine.ViewportChangeSource = "bind",
  ): void {
    const monthKey = this.#monthKeyForColumns(columns);

    const lastMonth = this.#lastBoundMonthByRowId.get(row.id);
    if (lastMonth === monthKey) {
      this.#cancelPendingMonthBind(row.id);
      return;
    }

    if (this.#shouldDebounceMonthBind(viewportChangeSource)) {
      this.#scheduleMonthBind(row, columns, handle, monthKey);
      return;
    }

    this.#bindMonth(row, columns, handle);
  }

  staticChildren(row: SheetRow.Joined.Handle): StaticChildSpec[] {
    const cal = this.#calendarsByRowId.get(row.id);
    if (!cal) return [];
    return [{ id: "calendar-month", $element: cal.$calendar, fillWidth: true }];
  }

  legendFactory: SheetLegendClass<SheetLegend.DefaultRow> = class {
    static make(): SheetLegend.DefaultRow {
      return SheetLegend.DefaultRow.make();
    }

    static bind(
      $legend: SheetLegend.DefaultRow,
      row: SheetRow.Joined.Handle,
      handle: SheetDelegate,
    ): void {
      $legend.bind(
        row,
        (target) => handle.rebind.revealRowOptionsPopover(row, target),
        handle,
        { defaultLabel: "Calendar Month" },
      );
    }

    static computeWidth = SheetLegend.DefaultFactory.computeWidth;
  };

  onResize() {}
  loadData = null;
  onBindRow() {}
  onRenderedColumnsWillChange() {}
}

/** Types and values that go with `GridLayoutPlugin`: `Attributes`, `height`. */
export namespace GridLayoutPlugin {
  export type Attributes = {};

  export function height(row: SheetRow.Joined.Handle): number {
    if (row.heightPx) return row.heightPx;
    return 154.5 * 6;
  }
}
